
'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { authApi, setToken, setUser, captchaApi, systemApi, getToken, getUser, clearToken } from '@/lib/api';
import { initWebSocket } from '@/lib/websocket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RefreshCw, LogIn, UserPlus, CheckCircle2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useFormOperation } from '@/lib/hooks/use-operation-state';
import { SliderCaptchaDialog } from '@/components/captcha-slider-dialog';
import { ClickCaptchaDialog } from '@/components/captcha-click-dialog';
import Image from 'next/image';
import { hitokotoApi } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { TurnstileCaptcha } from '@/components/turnstile-captcha';
import { POWWidget } from '@/components/pow-widget';

interface SystemInfo {
  name: string;
  description: string;
  logo_url?: string;
  allow_registration: boolean;
  captcha_enabled: boolean;
  captcha_type: 'digit' | 'slide' | 'pow' | 'click' | 'turnstile' | 'random';
  max_upload_size_mb: number;
  captcha_config?: {
    pow_difficulty?: number;
    pow_required_solutions?: number;
    pow_max_difficulty?: number;
    pow_expire_seconds?: number;
    rate_limit_window_seconds?: number;
    rate_limit_max_requests?: number;
    require_browser_environment?: boolean;
    browser_check_timeout?: number;
    verify_token_expire_minutes?: number;
  };
  turnstile_sitekey?: string;
}

export default function LoginPage() {
  const router = useRouter();
  const [isLogin, setIsLogin] = useState(true);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [captcha, setCaptcha] = useState<{ 
    id: string; 
    image?: string;
    master_image?: string; // 滑块验证码主图
    tile_image?: string; // 滑块验证码拼图块
    x?: number; // 滑块验证码X坐标
    y?: number; // 滑块验证码Y坐标
    images?: string[]; // 多图选择验证码的图片数组
    correct_indices?: number[]; // 多图选择验证码的正确索引
    type?: string;
    challenge?: unknown; // POW挑战数据
    sitekey?: string; // Turnstile sitekey
    text?: string; // 点击验证码提示文字
    total_images?: number; // 图片总数
  } | null>(null);
  const [clickDialogOpen, setClickDialogOpen] = useState(false);
  const [slideDialogOpen, setSlideDialogOpen] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaKey, setCaptchaKey] = useState(''); // 验证码验证成功后的key
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaFailCount, setCaptchaFailCount] = useState(0);
  const [powSolution, setPowSolution] = useState(''); // POW解决方案（自动生成）
  const [powComputing, setPowComputing] = useState(false); // POW计算中
  const [, setPowProgress] = useState<{ iterations: number; startTime: number; solutionsFound?: number; requiredSolutions?: number }>({ iterations: 0, startTime: 0 }); // POW计算进度（实时）
  const [captchaLoadingLock, setCaptchaLoadingLock] = useState(false); // 验证码加载锁，防止重复请求
  const [powChallengeData, setPowChallengeData] = useState<{
    captcha_id: string;
    challenge_id: string;
    nonce: string;
    salt: string;
    timestamp: number;
    difficulty: number;
    target: string;
    required_solutions?: number; // 需要的solution数量
  } | null>(null); // POW挑战数据
  const [isLoggedIn, setIsLoggedIn] = useState(false); // 登录状态，防止登录后继续请求验证码
  const [powSolutions, setPowSolutions] = useState<string[]>([]); // POW批量解决方案数组
  const [selectedImageIndices, setSelectedImageIndices] = useState<number[]>([]); // 用户选中的图片索引（新版本）
  
  // 使用新的状态管理Hook
  const loginOperation = useFormOperation();
  const registerOperation = useFormOperation();
  
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    email: '',
  });

  // 检查是否已登录，如果已登录则跳转
  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      const user = getUser();
      
      if (token && user) {
        try {
          // 验证token是否有效，并获取最新用户信息
          const profile = await authApi.getProfile() as { data?: { role?: string; username?: string } };
          if (profile && profile.data) {
            // 更新本地用户信息，确保角色信息同步
            const updatedUser = {
              ...user,
              role: profile.data.role || user.role,
              username: profile.data.username || user.username
            };
            setUser(updatedUser);
            
            // token有效，根据最新角色跳转
            const userRole = profile.data.role;
            if (userRole === 'teacher' || userRole === 'admin') {
              router.push('/teacher');
            } else if (userRole === 'student') {
              router.push('/student');
            } else {
              // 角色未知，清除token
              clearToken();
            }
          } else {
            // 响应格式不正确，清除token
            clearToken();
          }
        } catch (error) {
          // token无效或过期，清除本地存储
          console.error('Token validation failed:', error);
          clearToken();
        }
      }
    };
    
    checkAuth();
  }, [router]);

  // 环境识别：检查是否为浏览器环境（使用useMemo避免SSR问题）
  const isBrowserEnvironment = useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      if (typeof navigator === 'undefined') return false;
      if (typeof document === 'undefined') return false;
      if (typeof window.crypto === 'undefined') return false;
      if (typeof window.crypto.subtle === 'undefined') return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  // 加载系统信息和验证码
  useEffect(() => {
    loadSystemInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const loadCaptcha = useCallback(async () => {
    // 如果已经登录，不再请求验证码
    if (isLoggedIn) {
      console.debug('[Captcha] 跳过加载：用户已登录');
      return;
    }

    // 如果已经有验证key，不再请求新验证码
    if (captchaKey) {
      console.debug('[Captcha] 跳过加载：已有验证key', { captchaKey: captchaKey.substring(0, 8) + '...' });
      return;
    }

    // 如果验证失败次数过多，不再请求
    if (captchaFailCount >= 3) {
      console.warn('[Captcha] 跳过加载：失败次数过多', { captchaFailCount });
      return;
    }

    if (!systemInfo?.captcha_enabled) {
      console.debug('[Captcha] 跳过加载：验证码未启用');
      return;
    }
    
    // 使用锁防止并发请求
    if (captchaLoadingLock || captchaLoading) {
      console.debug('[Captcha] 跳过加载：正在加载中', { captchaLoadingLock, captchaLoading });
      return;
    }

    // 设置锁和加载状态
    setCaptchaLoadingLock(true);
    setCaptchaLoading(true);
    setCaptchaError('');

    try {
      // 如果类型为 random，传递 random 给后端；否则传递实际类型或空字符串让后端决定
      const captchaType = systemInfo.captcha_type === 'random' ? 'random' : systemInfo.captcha_type;
      const response = await captchaApi.generate(captchaType as 'digit' | 'slide' | 'pow' | 'click' | 'turnstile' | 'random') as {
        code?: number;
        message?: string;
        data?: { 
          id: string; 
          image?: string; 
          images?: string[];
          total_images?: number;
          type: string;
          challenge?: unknown;
          sitekey?: string;
          text?: string;
          expires_at?: string;
        };
        // 如果apiRequest直接返回data，也可能直接包含这些字段
        id?: string;
        type?: string;
        image?: string;
        images?: string[];
        total_images?: number;
        challenge?: unknown;
        sitekey?: string;
        text?: string;
        expires_at?: string;
      };
      
      // apiRequest返回的是整个响应对象 {code, message, data}
      // 需要从response.data中读取验证码数据
      interface CaptchaResponse {
        code?: number;
        message?: string;
        data?: {
          id: string;
          type?: string;
          image?: string;
          master_image?: string;
          tile_image?: string;
          x?: number;
          y?: number;
          images?: string[];
          correct_indices?: number[];
          challenge?: unknown;
          sitekey?: string;
          text?: string;
          total_images?: number;
          positions?: Array<{ x: number; y: number }>;
          expires_at?: string;
        };
        id?: string;
        type?: string;
        image?: string;
        master_image?: string;
        tile_image?: string;
        x?: number;
        y?: number;
        images?: string[];
        correct_indices?: number[];
        challenge?: unknown;
        sitekey?: string;
        text?: string;
        total_images?: number;
        positions?: Array<{ x: number; y: number }>;
        expires_at?: string;
      }
      const captchaResponse = response as CaptchaResponse;
      const captchaResponseData = captchaResponse.data || captchaResponse;
      
      if (!captchaResponseData || (!captchaResponseData.id)) {
        throw new Error('验证码响应格式错误');
      }
      
      const captchaData: typeof captcha = {
        id: captchaResponseData.id,
        type: captchaResponseData.type || systemInfo.captcha_type || 'digit', // 确保有type值
      };
      
      
      // 根据类型设置不同的数据
      if (captchaResponseData.image) {
        captchaData.image = captchaResponseData.image;
      }
      // 滑块验证码新格式
      if (captchaResponseData.master_image) {
        captchaData.master_image = captchaResponseData.master_image;
      }
      if (captchaResponseData.tile_image) {
        captchaData.tile_image = captchaResponseData.tile_image;
      }
      if (captchaResponseData.x !== undefined) {
        captchaData.x = captchaResponseData.x;
      }
      if (captchaResponseData.y !== undefined) {
        captchaData.y = captchaResponseData.y;
      }
      if (captchaResponseData.images && Array.isArray(captchaResponseData.images)) {
        captchaData.images = captchaResponseData.images;
      }
      if (captchaResponseData.correct_indices && Array.isArray(captchaResponseData.correct_indices)) {
        captchaData.correct_indices = captchaResponseData.correct_indices;
      }
      if (captchaResponseData.challenge) {
        captchaData.challenge = captchaResponseData.challenge;
      }
      if (captchaResponseData.sitekey) {
        captchaData.sitekey = captchaResponseData.sitekey;
      }
      if (captchaResponseData.text) {
        captchaData.text = captchaResponseData.text;
      }
      if (captchaResponseData.total_images) {
        captchaData.total_images = captchaResponseData.total_images;
      }
      
      setCaptcha(captchaData);
      
      // 清空相关状态（但保留验证key和失败计数，避免重复请求）
      setCaptchaCode('');
      setSelectedImageIndices([]);
      // 如果已经有验证key，保留powSolution以显示验证成功状态
      if (!captchaKey) {
        setPowSolution(''); // 确保解决方案被清空
        setPowSolutions([]); // 清空批量解决方案
      }
      setPowComputing(false);
      setPowProgress({ iterations: 0, startTime: 0, solutionsFound: 0, requiredSolutions: 0 }); // 清空进度
      // 如果已经有验证key，不清空挑战数据，保持验证成功状态
      if (!captchaKey) {
        setPowChallengeData(null);
      }
      
      // POW类型：POWWidget会自动处理挑战
      if (captchaData.type === 'pow' && captchaResponseData.challenge) {
        // POWWidget组件会自动获取挑战并计算，这里不需要额外处理
        console.debug('[Captcha] POW验证码已加载，等待组件处理');
      }
      
    } catch (error) {
      // 发生错误时，统一显示错误信息
      console.error('[Captcha] 验证码加载失败', { 
        error: error instanceof Error ? error.message : String(error),
        type: systemInfo?.captcha_type,
        timestamp: new Date().toISOString()
      });
      setCaptchaError('验证码加载失败');
      toast.error('验证码加载失败，请重试');
      
      // 仍然设置一个空的captcha对象（如果验证码启用）
      if (systemInfo?.captcha_enabled) {
        setCaptcha({ id: '', type: systemInfo.captcha_type });
      }
    } finally {
      setCaptchaLoading(false);
      // 延迟释放锁，防止快速连续请求（使用配置的时间，但最小1秒）
      const rateLimitWindow = Math.max(
        systemInfo?.captcha_config?.rate_limit_window_seconds || 2,
        1
      );
      setTimeout(() => {
        setCaptchaLoadingLock(false);
      }, rateLimitWindow * 1000);
    }
  }, [systemInfo?.captcha_enabled, systemInfo?.captcha_type, systemInfo?.captcha_config, isLoggedIn, captchaKey, captchaFailCount, captchaLoading, captchaLoadingLock]);

  // 使用ref存储startAutoPOWComputation函数引用，避免循环依赖（必须在定义之前初始化）
  const startAutoPOWComputationRef = useRef<((challengeData: {
    captcha_id: string;
    challenge_id: string;
    nonce: string;
    salt: string;
    timestamp: number;
    difficulty: number;
    target: string;
    required_solutions?: number;
  }) => Promise<void>) | null>(null);

  // POW无感通过：后台静默计算函数（支持批量solutions阵列）
  const startAutoPOWComputation = useCallback(async (challengeData: {
    captcha_id: string;
    challenge_id: string;
    nonce: string;
    salt: string;
    timestamp: number;
    difficulty: number;
    target: string;
    required_solutions?: number;
  }) => {
    // 如果已经登录或已经在计算，或已经有验证key，不启动
    if (isLoggedIn || powComputing || captchaKey) {
      return;
    }
    
    // 如果验证失败次数过多，不再计算
    if (captchaFailCount >= 3) {
      return;
    }

    // 使用配置中的required solutions（减少计算量，1-2秒内完成验证）
    // 降低默认值，从32降到2，确保快速完成
    const requiredSolutions = Math.min(
      challengeData.required_solutions || systemInfo?.captcha_config?.pow_required_solutions || 2,
      2 // 最大不超过2个，确保快速完成
    );
    const startTime = Date.now();
    setPowComputing(true);
    setPowProgress({ iterations: 0, startTime, solutionsFound: 0, requiredSolutions });
    
    try {
      // 批量计算多个solution（阵列函数）
      const solutions: number[] = []; // 存储为数字数组，直接返回给后端
      // 使用配置中的参数计算最大尝试次数（1-2秒内完成验证）
      // 降低难度到2，加快计算速度
      const difficulty = Math.min(
        challengeData.difficulty || systemInfo?.captcha_config?.pow_difficulty || 2,
        2 // 最大难度不超过2，确保快速完成
      );
      const probabilityPerAttempt = 1 / Math.pow(16, difficulty);
      const avgIterationsPerSolution = Math.ceil(1 / probabilityPerAttempt);
      // 安全系数1.2倍
      const maxAttempts = Math.ceil(avgIterationsPerSolution * requiredSolutions * 1.2);
      let totalIterations = 0;
      
      // 使用Web Crypto API批量计算（阵列函数）
      // 使用降低后的难度计算targetPrefix
      const targetPrefix = '0'.repeat(difficulty);
      const encoder = new TextEncoder();
      
      // 并发计算多个solution（使用Promise.all限制并发数）
      const computeBatch = async (batchSize: number = 100): Promise<number[]> => {
        const batch: Promise<number | null>[] = [];
        let attemptCount = 0;
        
        while (solutions.length < requiredSolutions && attemptCount < maxAttempts && !isLoggedIn) {
          // 如果已经有验证key，停止计算
          if (captchaKey) {
            break;
          }
          
          // 创建一批并发计算任务
          for (let i = 0; i < batchSize && solutions.length < requiredSolutions && attemptCount < maxAttempts; i++) {
            // 如果已经有验证key，停止创建新任务
            if (captchaKey) {
              break;
            }
            const solutionNum = attemptCount + i;
            batch.push(
              (async (num: number): Promise<number | null> => {
                const solution = num.toString();
                const computeString = `${challengeData.challenge_id}:${challengeData.nonce}:${challengeData.salt}:${challengeData.timestamp}:${solution}`;
                
                const data1 = encoder.encode(computeString);
                const hashBuffer1 = await crypto.subtle.digest('SHA-256', data1);
                const hashArray1 = Array.from(new Uint8Array(hashBuffer1));
                const firstHash = hashArray1.map(b => b.toString(16).padStart(2, '0')).join('');
                
                const reversedSolution = solution.split('').reverse().join('');
                const data2 = encoder.encode(firstHash + reversedSolution);
                const hashBuffer2 = await crypto.subtle.digest('SHA-256', data2);
                const hashArray2 = Array.from(new Uint8Array(hashBuffer2));
                const secondHash = hashArray2.map(b => b.toString(16).padStart(2, '0')).join('');
                
                if (secondHash.startsWith(targetPrefix)) {
                  return num; // 直接返回数字
                }
                return null;
              })(solutionNum)
            );
            attemptCount++;
          }
          
          // 等待这批任务完成
          const results = await Promise.all(batch);
          
          for (const result of results) {
            if (result !== null && solutions.length < requiredSolutions) {
              // 检查是否已存在该solution，避免重复
              if (!solutions.includes(result)) {
                solutions.push(result); // 存储为数字
              }
            }
          }
          batch.length = 0;
          
          // 更新进度（每批次都更新，基于实际找到的solution数量）
          totalIterations += batchSize;
          // 实时更新进度，基于实际找到的solution数量
          setPowProgress(prev => ({
            iterations: totalIterations,
            solutionsFound: solutions.length,
            requiredSolutions: requiredSolutions,
            startTime: prev.startTime > 0 ? prev.startTime : startTime,
          }));
          
          // 减少延迟，加快计算速度（只在必要时让出控制权，减少到每10批次一次）
          if (totalIterations % (batchSize * 10) === 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
        
        return solutions;
      };
      
      const computedSolutions = await computeBatch();
      
      if (computedSolutions.length >= requiredSolutions && !isLoggedIn && isBrowserEnvironment) {
        // 存储为数字数组（阵列函数），同时准备点阵数组格式
        setPowSolutions(computedSolutions.map(s => s.toString()));
        // 确保有solution显示验证状态
        if (!powSolution && computedSolutions.length > 0) {
          setPowSolution(computedSolutions[0].toString());
        }
        
        // 转换为点阵数组格式（二维数组）
        const pointMatrixArray: number[][] = computedSolutions.map(sol => {
          // 将solution转换为32位的二进制点阵数组
          const binary = sol.toString(2).padStart(32, '0');
          return binary.split('').map(bit => parseInt(bit));
        });
        
        // 自动验证（使用批量solution点阵数组）
        try {
          const verifyResponse = await captchaApi.verify(
            challengeData.captcha_id,
            undefined,
            undefined,
            undefined,
            undefined,
            pointMatrixArray
          ) as { verified: boolean; key: string } | { data: { verified: boolean; key: string } };
          
          const verified = 'data' in verifyResponse ? verifyResponse.data.verified : verifyResponse.verified;
          const key = 'data' in verifyResponse ? verifyResponse.data.key : verifyResponse.key;
          
          if (verified && key) {
            // 立即停止计算状态，显示成功
            setPowComputing(false);
            setCaptchaKey(key);
            setCaptchaError('');
            setCaptchaFailCount(0);
            // 保留powSolution以便显示验证成功状态
            // 只清空挑战数据和进度，防止重复计算
            setPowChallengeData(null);
            setPowProgress({ iterations: 0, startTime: 0, solutionsFound: 0, requiredSolutions: 0 });
            // 确保有solution显示，如果还没有则使用第一个
            if (computedSolutions.length > 0 && !powSolution) {
              setPowSolution(computedSolutions[0].toString());
            }
            // 静默成功，不显示toast（无感通过）
          } else {
            // 验证失败，增加失败计数
            setCaptchaFailCount(prev => prev + 1);
            setCaptchaError('验证失败，请重试');
            setPowComputing(false);
            // 验证失败后，清空挑战数据，防止重复计算
            setPowChallengeData(null);
          }
        } catch {
          // 验证失败，增加失败计数
          setCaptchaFailCount(prev => prev + 1);
          setCaptchaError('验证失败，请重试');
          setPowComputing(false);
          // 验证失败后，清空挑战数据，防止重复计算
          setPowChallengeData(null);
        }
      } else {
        setPowComputing(false);
        // 如果计算失败，清空挑战数据，防止重复计算
        if (computedSolutions.length === 0) {
          setPowChallengeData(null);
        }
      }
    } catch (error) {
      console.error('[Captcha] POW计算异常', { 
        error: error instanceof Error ? error.message : String(error),
        challenge_id: challengeData.challenge_id?.substring(0, 8) + '...',
        timestamp: new Date().toISOString()
      });
      setPowComputing(false);
    }
  }, [isLoggedIn, powComputing, captchaKey, isBrowserEnvironment, systemInfo?.captcha_config?.pow_difficulty, systemInfo?.captcha_config?.pow_required_solutions, captchaFailCount, powSolution]);

  // 更新ref中的函数引用
  useEffect(() => {
    startAutoPOWComputationRef.current = startAutoPOWComputation;
  }, [startAutoPOWComputation]);

  // 当powChallengeData设置后，自动触发POW计算（备用触发机制）
  // 注意：主要触发在loadCaptcha中，这里作为备用
  useEffect(() => {
    // 如果已经有验证key或失败次数过多，不触发计算
    if (captchaKey || captchaFailCount >= 3 || isLoggedIn) {
      return;
    }
    
    // 检查条件：有挑战数据、未在计算、浏览器环境、POW类型、ref已就绪
    if (powChallengeData && !powComputing && isBrowserEnvironment && captcha?.type === 'pow' && typeof window !== 'undefined' && startAutoPOWComputationRef.current) {
      // 立即设置计算状态，显示加载动画
      setPowComputing(true);
      setPowProgress({ iterations: 0, startTime: Date.now(), solutionsFound: 0, requiredSolutions: powChallengeData?.required_solutions || 2 });
      
      // 使用setTimeout确保在下一个事件循环中执行
      const timer = setTimeout(() => {
        if (!captchaKey && captchaFailCount < 3 && !isLoggedIn && powChallengeData && startAutoPOWComputationRef.current) {
          startAutoPOWComputationRef.current(powChallengeData).catch(error => {
            console.error('[Captcha] POW计算执行失败', error);
            setPowComputing(false);
          });
        } else {
          setPowComputing(false);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [powChallengeData, isLoggedIn, powComputing, isBrowserEnvironment, captcha?.type, captchaKey, captchaFailCount]);

  // 当系统信息加载完成后，加载验证码（仅在启用时）
  useEffect(() => {
    // 如果已经登录，不再加载验证码
    if (isLoggedIn) {
      return;
    }
    
    // 如果已经有验证key，不再加载新验证码
    if (captchaKey) {
      return;
    }
    
    // 如果验证失败次数过多，不再加载
    if (captchaFailCount >= 3) {
      return;
    }
    
    const captchaEnabled = systemInfo?.captcha_enabled || false;
    
    if (systemInfo && captchaEnabled) {
      // 只在没有验证码或验证码ID为空时加载
      if (!captcha || !captcha.id) {
        loadCaptcha();
      }
    } else if (systemInfo && !captchaEnabled) {
      // 后端关闭验证码时，清空验证码状态
      setCaptcha(null);
      setCaptchaKey('');
      setCaptchaCode('');
      setCaptchaError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemInfo?.captcha_enabled, isLoggedIn]);

  const loadSystemInfo = async () => {
    try {
      const response = await systemApi.getInfo() as { data: SystemInfo };
      if (response?.data) {
        setSystemInfo(response.data);
        
        // 如果禁止注册且当前在注册页面，自动切换到登录页面
        if (!response.data.allow_registration && !isLogin) {
          setIsLogin(true);
          toast.info('系统已禁用注册功能');
        }
      }
    } catch {
      toast.error('系统信息加载失败');
      // 系统信息加载失败时，不设置默认值，让系统保持禁用状态
      setSystemInfo(null);
    }
  };

  // 验证验证码（支持所有类型）- 使用useCallback避免循环依赖
  const verifyCaptcha = useCallback(async (
    code?: string, 
    solution?: string, 
    positions?: Array<{ x: number; y: number }>, 
    selectedIndices?: number[], 
    solutions?: number[] | number[][]
  ): Promise<string | null> => {
    // POW验证码环境检测：如果环境异常，直接失败
    if (captcha?.type === 'pow') {
      if (!isBrowserEnvironment) {
        console.error('POW验证码：浏览器环境检测失败');
        toast.error('POW验证码需要浏览器环境');
        setCaptchaError('浏览器环境检测失败');
        setPowComputing(false);
        return null;
      }
      // 检查必要的浏览器API
      if (!window.crypto || !window.crypto.subtle) {
        console.error('POW验证码：缺少必要的加密API');
        toast.error('POW验证码需要加密API支持');
        setCaptchaError('缺少加密API支持');
        setPowComputing(false);
        return null;
      }
    }
    // 环境检查：如果要求浏览器环境但检测失败，直接失败
    if (systemInfo?.captcha_config?.require_browser_environment !== false) {
      if (!isBrowserEnvironment) {
        console.error('验证码只能在浏览器环境中使用');
        toast.error('验证码只能在浏览器环境中使用');
        setCaptchaError('浏览器环境检测失败');
        return null;
      }
    }

    // 如果已经登录，不再验证验证码
    if (isLoggedIn) {
      return captchaKey || null;
    }
    if (!systemInfo?.captcha_enabled || !captcha) {
      return null; // 验证码未启用或未加载
    }

    const captchaType = captcha.type || systemInfo.captcha_type;
    
    // 根据类型检查必要的参数
    if (captchaType === 'pow' && !solution && !powSolution) {
      toast.error('POW验证码需要解决方案');
      return null;
    }
    if (captchaType === 'click') {
      // 仅支持多图片选择模式
      if (!selectedIndices || selectedIndices.length === 0) {
        toast.error('请选择正确的图片');
        return null;
      }
    }
    if (captchaType === 'turnstile') {
      // Turnstile由组件处理，直接返回已有key
      if (captchaKey) return captchaKey;
      toast.error('安全验证未完成');
      return null;
    }
    if ((captchaType === 'digit' || captchaType === 'slide') && !code && !captchaCode) {
      toast.error('请完成验证码验证');
      return null;
    }

    try {
      const codeToVerify = code || captchaCode;
      const finalSelectedIndices = selectedIndices !== undefined ? selectedIndices : selectedImageIndices;
      
      // 处理POW验证码：仅支持批量solutions（点阵数组）
      let powSolutionsToSend: number[][] | undefined = undefined;
      
      if (captchaType === 'pow') {
        // 仅支持批量solutions（点阵数组格式）
        if (solutions && solutions.length > 0) {
          // 如果是点阵数组（二维数组），直接使用
          if (Array.isArray(solutions[0])) {
            powSolutionsToSend = solutions as number[][];
          } else {
            // 如果是简单数字数组，转换为点阵数组格式
            powSolutionsToSend = (solutions as number[]).map(sol => {
              // 将solution转换为点阵数组（32位二进制点阵）
              const binary = sol.toString(2).padStart(32, '0');
              return binary.split('').map(bit => parseInt(bit));
            });
          }
        } else if (powSolutions.length > 0) {
          // 将字符串数组转换为点阵数组
          powSolutionsToSend = powSolutions.map(s => {
            const num = parseInt(s);
            const binary = num.toString(2).padStart(32, '0');
            return binary.split('').map(bit => parseInt(bit));
          });
        } else {
          // POW验证码必须提供solutions（点阵数组）
          toast.error('POW验证码需要解决方案');
          return null;
        }
      }
      
      const verifyResponse = await captchaApi.verify(
        captcha.id,
        captchaType === 'digit' || captchaType === 'slide' ? codeToVerify : undefined,
        undefined, // POW不再支持单个solution
        undefined, // 不再支持旧版本坐标点
        captchaType === 'click' ? finalSelectedIndices : undefined,
        powSolutionsToSend
      ) as { verified: boolean; key: string } | { data: { verified: boolean; key: string } };
      
      // 处理不同的响应格式
      const verified = 'data' in verifyResponse ? verifyResponse.data.verified : verifyResponse.verified;
      const key = 'data' in verifyResponse ? verifyResponse.data.key : verifyResponse.key;
      
      if (verified) {
        setCaptchaKey(key);
        setCaptchaFailCount(0);
        setCaptchaError('');
        return key;
      } else {
        throw new Error('验证码验证失败');
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
      
      // 检查是否是频率限制错误（429）
      if (error?.response?.status === 429) {
        setCaptchaFailCount(prev => prev + 1);
        toast.error('验证失败次数过多，请稍后再试');
        setCaptchaError('验证失败次数过多，请稍后再试');
        // 429错误不自动刷新验证码，让用户等待
        return null;
      }
      
      // 检查是否是验证码过期错误
      const errorMessage = error?.message || '';
      if (error?.response?.data?.message?.includes('expired') || errorMessage.includes('expired')) {
        const newFailCount = captchaFailCount + 1;
        console.warn('[Captcha] 验证码已过期', { 
          captcha_id: captcha?.id,
          fail_count: newFailCount
        });
        setCaptchaFailCount(prev => prev + 1);
        toast.error('验证码已过期，请刷新验证码');
        setCaptchaError('验证码已过期，请刷新验证码');
        // 验证码过期时，不再自动刷新（避免高频请求）
        return null;
      }
      
      // 其他错误
      const newFailCount = captchaFailCount + 1;
      console.error('[Captcha] 验证码验证失败', { 
        captcha_id: captcha?.id,
        error: errorMessage,
        fail_count: newFailCount,
        timestamp: new Date().toISOString()
      });
      setCaptchaFailCount(prev => prev + 1);
      toast.error('验证码错误，请重新验证');
      setCaptchaError('验证码错误');
      
      // 验证失败后不再自动刷新验证码（避免高频请求）
      // 用户需要手动点击刷新按钮
      
      return null;
    }
  }, [isLoggedIn, isBrowserEnvironment, systemInfo, captcha, captchaCode, captchaKey, powSolutions, selectedImageIndices, powSolution, captchaFailCount]);

  // 处理滑块验证码验证
  const handleSliderVerify = async (position: number): Promise<boolean> => {
    // 如果已经登录，不再验证
    if (isLoggedIn) {
      return true;
    }
    // 调用验证函数，传入位置（X坐标）
    const key = await verifyCaptcha(`${position}:0`); // 格式：X:Y
    if (key) {
      // 验证成功，设置key，不再刷新验证码
      setCaptchaKey(key);
      setCaptchaError('');
      setCaptchaFailCount(0);
      return true;
    }
    return false;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    await loginOperation.submitForm(async () => {
      let finalCaptchaKey: string | null = captchaKey;
      
      // 只有在启用验证码时才需要验证
      if (systemInfo?.captcha_enabled) {
        // 对于POW验证码，如果已经有solution但还没有key，先验证
        if (captcha?.type === 'pow' && powSolution && !finalCaptchaKey) {
          console.log('🔐 POW验证码有解决方案但未验证，正在验证...');
          finalCaptchaKey = await verifyCaptcha(undefined, powSolution);
          if (!finalCaptchaKey) {
            throw new Error('POW验证失败，请点击复选框完成验证');
          }
        } else if (!finalCaptchaKey) {
          console.log('🔐 验证码未验证，自动验证中...');
          finalCaptchaKey = await verifyCaptcha();
          if (!finalCaptchaKey) {
            throw new Error('验证码验证失败');
          }
        }
      }

      const response = await authApi.login({
        username: loginForm.username,
        password: loginForm.password,
        captcha_key: finalCaptchaKey,
      });

      
      // 保存token和用户信息（支持刷新token）
      const loginResponseData = response as { 
        data: { 
          token?: string;
          access_token?: string;
          refresh_token?: string;
          user: { role?: string; id?: number; username?: string } 
        } 
      };
      const loginToken = loginResponseData.data.access_token || loginResponseData.data.token;
      const refreshToken = loginResponseData.data.refresh_token;
      const loginUser = loginResponseData.data.user;
      
      // 设置token（包括刷新token）
      if (loginToken) {
        setToken(loginToken, refreshToken);
      }
      
      // 确保用户信息完整
      const completeUser = {
        id: loginUser.id,
        username: loginUser.username,
        role: loginUser.role
      };
      setUser(completeUser);
      
      console.log('User info saved:', completeUser);
      
      // 设置登录状态，阻止后续验证码请求
      setIsLoggedIn(true);
      
      // 初始化WebSocket连接
      try {
        initWebSocket();
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
      }
      
      // 显示一言提示
      await showHitokotoAfterLogin();
      
      // 根据角色跳转到对应页面
      const loginUserRole = loginUser.role;
      if (loginUserRole === 'teacher' || loginUserRole === 'admin') {
        router.push('/teacher');
      } else if (loginUserRole === 'student') {
        router.push('/student');
      } else {
        toast.error('未知的用户角色');
      }
      
      return loginResponseData.data;
    }, {
      successMessage: '登录成功',
      errorMessage: '登录失败',
      resetForm: () => {
        // 只在登录成功时清空表单，但不再请求验证码（因为已经登录）
        setLoginForm({ username: '', password: '' });
        setCaptchaCode('');
        setCaptchaKey('');
        // 不再调用loadCaptcha()，避免登录后继续请求验证码
      }
    });
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    await registerOperation.submitForm(async () => {
      let finalCaptchaKey: string | null = captchaKey;
      
      // 只有在启用验证码时才需要验证
      if (systemInfo?.captcha_enabled) {
        // 对于POW验证码，如果已经有solution但还没有key，先验证
        if (captcha?.type === 'pow' && powSolution && !finalCaptchaKey) {
          console.log('🔐 POW验证码有解决方案但未验证，正在验证...');
          finalCaptchaKey = await verifyCaptcha(undefined, powSolution);
          if (!finalCaptchaKey) {
            throw new Error('POW验证失败，请点击复选框完成验证');
          }
        } else if (!finalCaptchaKey) {
          console.log('🔐 验证码未验证，自动验证中...');
          finalCaptchaKey = await verifyCaptcha();
          if (!finalCaptchaKey) {
            throw new Error('验证码验证失败');
          }
        }
      }

      const response = await authApi.register({
        username: registerForm.username,
        password: registerForm.password,
        email: registerForm.email,
        captcha_key: finalCaptchaKey,
      });

      
      // 保存token和用户信息（支持刷新token）
      const responseData = response as { 
        data: { 
          token?: string;
          access_token?: string;
          refresh_token?: string;
          user: { role?: string; id?: number; username?: string } 
        } 
      };
      const token = responseData.data.access_token || responseData.data.token;
      const refreshToken = responseData.data.refresh_token;
      const user = responseData.data.user;
      
      // 设置token（包括刷新token）
      if (token) {
        setToken(token, refreshToken);
      }
      
      // 确保用户信息完整
      const completeUser = {
        id: user.id,
        username: user.username,
        role: user.role
      };
      setUser(completeUser);
      
      console.log('User info saved:', completeUser);
      
      // 设置登录状态，阻止后续验证码请求
      setIsLoggedIn(true);
      
      // 初始化WebSocket连接
      try {
        initWebSocket();
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
      }
      
      // 根据角色跳转到对应页面
      const userRole = user.role;
      if (userRole === 'teacher' || userRole === 'admin') {
        router.push('/teacher');
      } else if (userRole === 'student') {
        router.push('/student');
      } else {
        toast.error('未知的用户角色');
      }
      
      return responseData.data;
    }, {
      successMessage: '注册成功',
      errorMessage: '注册失败',
      resetForm: () => {
        // 只在注册成功时清空表单，但不再请求验证码（因为已经登录）
        setRegisterForm({
          username: '',
          password: '',
          email: '',
        });
        setCaptchaCode('');
        setCaptchaKey('');
        // 不再调用loadCaptcha()，避免登录后继续请求验证码
      }
    });
  };

  // 登录成功后显示一言提示
  const showHitokotoAfterLogin = async () => {
    try {
      const response = await hitokotoApi.get() as { data: { hitokoto: string; from: string; from_who?: string } };
      const hitokoto = response.data;
      const source = hitokoto.from_who ? `${hitokoto.from_who}《${hitokoto.from}》` : hitokoto.from;
      toast.info(`${hitokoto.hitokoto}\n—— ${source}`, {
        duration: 5000,
        position: 'top-center',
      });
    } catch {
      // 如果获取失败，静默失败，不显示错误
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-3 py-4 sm:px-4 sm:py-6 md:py-8">
      <div className="w-full max-w-md space-y-3 sm:space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 md:p-8">
          {/* Logo和标题 */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
              考试系统
            </h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
              {isLogin ? '登录到您的账户' : '创建新账户'}
            </p>
          </div>

          {/* 错误提示 */}
          {(loginOperation.error || registerOperation.error) && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
              {(isLogin ? loginOperation.error : registerOperation.error) || ''}
            </div>
          )}

          {/* 成功提示 */}
          {(loginOperation.success || registerOperation.success) && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400">
              {isLogin ? "登录成功，正在跳转..." : "注册成功，正在跳转..."}
            </div>
          )}

          {/* 登录表单 */}
          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-2.5 sm:space-y-3 md:space-y-4">
              <div>
                <Label htmlFor="login-username" className="text-xs sm:text-sm">用户名</Label>
                <Input
                  id="login-username"
                  type="text"
                  required
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder="请输入用户名"
                  className="mt-1 h-10 sm:h-9 text-base sm:text-sm"
                />
              </div>

              <div>
                <Label htmlFor="login-password" className="text-xs sm:text-sm">密码</Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="请输入密码"
                  className="mt-1 h-10 sm:h-9 text-base sm:text-sm"
                />
              </div>

              {/* 验证码（仅在后端启用时显示） */}
              {systemInfo?.captcha_enabled && captcha && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="captcha" className="text-xs sm:text-sm">
                      {captcha.type === 'slide' ? '滑块验证码' : 
                       captcha.type === 'pow' ? 'POW验证码' :
                       captcha.type === 'click' ? '图片点选验证码' :
                       captcha.type === 'turnstile' ? '安全验证 (Turnstile)' :
                       captcha.type === 'digit' ? '数字验证码' :
                       '图片验证码'}
                    </Label>
                    {systemInfo.captcha_type === 'random' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        随机验证码
                      </span>
                    )}
                  </div>
                  {captcha.type === 'slide' ? (
                    <div className="mt-1">
                      <div 
                        className={`relative border rounded-lg overflow-hidden bg-white dark:bg-gray-800 transition-all ${
                          captchaKey
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/10 shadow-sm cursor-default'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!captchaKey && !captchaLoading && captcha.image) {
                            setSlideDialogOpen(true);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="点击完成滑块验证"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!captchaKey && !captchaLoading && captcha.image) {
                              setSlideDialogOpen(true);
                            }
                          }
                        }}
                      >
                        <div className="p-3.5">
                          <div className="flex items-center justify-between gap-3">
                            {/* 左侧：复选框和文字 */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* 自定义复选框 */}
                              <div className="flex-shrink-0">
                                {captchaKey ? (
                                  // 已验证：显示选中状态的复选框
                                  <div className="h-7 w-7 border-2 border-green-600 bg-green-600 rounded-[4px] flex items-center justify-center pointer-events-none checkbox">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                  </div>
                                ) : (
                                  // 未选中：显示空复选框（灰色边框，白色背景）
                                  <div className="h-7 w-7 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-[4px] flex items-center justify-center pointer-events-none checkbox"></div>
                                )}
                              </div>
                              
                              {/* 文字 */}
                              <div className="flex-1 min-w-0 pointer-events-none">
                                <p 
                                  className={`text-sm font-medium transition-colors ${
                                    captchaKey
                                      ? 'text-green-600 dark:text-green-500'
                                      : 'text-gray-900 dark:text-white'
                                  }`}
                                >
                                  {captchaKey
                                    ? '恭喜您，验证通过！'
                                    : '继续以完成验证...'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  滑块验证
                                </p>
                              </div>
                            </div>
                            
                            {/* 右侧：标识 */}
                            <div className="flex items-center gap-2 flex-shrink-0 pointer-events-none">
                              <div className="flex items-center justify-center w-9 h-9 rounded pointer-events-none">
                                {captchaKey ? (
                                  // 已验证：绿色勾选
                                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ fill: '#63c08e', stroke: '#4CAF50' }}>
                                    <path d="M20 6L9 17l-5-5"/>
                                  </svg>
                                ) : (
                                  // 未选中状态：显示灰色圆形
                                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"></div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className={`text-xs font-semibold ${
                                  captchaKey
                                    ? 'text-green-600 dark:text-green-500'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  Slider
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {captcha.image && (
                        <SliderCaptchaDialog
                          open={slideDialogOpen}
                          onOpenChange={setSlideDialogOpen}
                          image={captcha.image}
                          onVerify={async (position) => {
                            if (isLoggedIn) return true;
                            const success = await handleSliderVerify(position);
                            if (success) {
                              // 立即关闭对话框，不延迟
                              setSlideDialogOpen(false);
                              return true;
                            }
                            return false;
                          }}
                          onRefresh={loadCaptcha}
                        />
                      )}
                    </div>
                  ) : captcha.type === 'pow' ? (
                    <div className="mt-1">
                      {/* 使用新的POW Widget组件 */}
                      {captchaKey ? (
                        <div className="border border-green-500 bg-green-50 dark:bg-green-900/10 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <p className="text-sm font-medium text-green-600 dark:text-green-500">
                              验证通过！
                            </p>
                          </div>
                        </div>
                      ) : (
                        <POWWidget
                          endpoint="/api/captcha"
                          onSolve={async (token) => {
                            try {
                              // cap.js返回的token是redeem后的验证token，格式为 "id:vertoken"
                              // 这个token可以直接作为captcha_key使用
                              // redeem已经验证了POW解决方案，返回的token就是验证token
                              setCaptchaKey(token);
                              setCaptchaError('');
                              setCaptchaFailCount(0);
                              toast.success('验证成功');
                            } catch (error) {
                              console.error('POW验证失败:', error);
                              setCaptchaError('验证失败，请重试');
                              toast.error('验证失败，请重试');
                              setCaptchaFailCount(prev => prev + 1);
                            }
                          }}
                          onError={(message) => {
                            setCaptchaError(message);
                            toast.error(message);
                            setCaptchaFailCount(prev => prev + 1);
                          }}
                        />
                      )}
                      
                      {/* 错误提示和重置按钮 */}
                      {(captchaError || captchaFailCount > 0) && !captchaKey && (
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-xs text-red-500">{captchaError || `验证失败 ${captchaFailCount} 次`}</p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setCaptchaKey('');
                              setCaptchaError('');
                              setCaptchaFailCount(0);
                              loadCaptcha();
                            }}
                            className="h-6 text-xs px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            重置验证
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : captcha.type === 'click' ? (
                    <div className="mt-1">
                      {captcha.images && captcha.images.length > 0 ? (
                        // 新版本：多图片选择模式（弹窗）
                        <div>
                          <div 
                            className={`relative border rounded-lg overflow-hidden bg-white dark:bg-gray-800 transition-all ${
                              captchaKey
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/10 shadow-sm cursor-default'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md cursor-pointer'
                            }`}
                            onClick={() => {
                              if (!captchaKey && !captchaLoading && captcha.images && captcha.images.length > 0) {
                                setClickDialogOpen(true);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="点击完成图片验证"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!captchaKey && !captchaLoading && captcha.images && captcha.images.length > 0) {
                                  setClickDialogOpen(true);
                                }
                              }
                            }}
                          >
                            <div className="p-3.5">
                              <div className="flex items-center justify-between gap-3">
                                {/* 左侧：复选框和文字 */}
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  {/* 自定义复选框 */}
                                  <div className="flex-shrink-0">
                                    {captchaKey ? (
                                      // 已验证：显示选中状态的复选框
                                      <div className="h-7 w-7 border-2 border-green-600 bg-green-600 rounded-[4px] flex items-center justify-center pointer-events-none checkbox">
                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                      </div>
                                    ) : (
                                      // 未选中：显示空复选框（灰色边框，白色背景）
                                      <div className="h-7 w-7 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-[4px] flex items-center justify-center pointer-events-none checkbox"></div>
                                    )}
                                  </div>
                                  
                                  {/* 文字 */}
                                  <div className="flex-1 min-w-0 pointer-events-none">
                                    <p 
                                      className={`text-sm font-medium transition-colors ${
                                        captchaKey
                                          ? 'text-green-600 dark:text-green-500'
                                          : 'text-gray-900 dark:text-white'
                                      }`}
                                    >
                                      {captchaKey
                                        ? '恭喜您，验证通过！'
                                        : '继续以完成验证...'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      图片验证
                                    </p>
                                  </div>
                                </div>
                                
                                {/* 右侧：标识 */}
                                <div className="flex items-center gap-2 flex-shrink-0 pointer-events-none">
                                  <div className="flex items-center justify-center w-9 h-9 rounded pointer-events-none">
                                    {captchaKey ? (
                                      // 已验证：绿色勾选
                                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ fill: '#63c08e', stroke: '#4CAF50' }}>
                                        <path d="M20 6L9 17l-5-5"/>
                                      </svg>
                                    ) : (
                                      // 未选中状态：显示灰色圆形
                                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"></div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-xs font-semibold ${
                                      captchaKey
                                        ? 'text-green-600 dark:text-green-500'
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                      Click
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {captcha.images && captcha.correct_indices && captcha.text && (
                            <ClickCaptchaDialog
                              open={clickDialogOpen}
                              onOpenChange={setClickDialogOpen}
                              images={captcha.images}
                              correctIndices={captcha.correct_indices}
                              text={captcha.text}
                              onVerify={async (selectedIndices) => {
                                // 如果已经登录，不再验证
                                if (isLoggedIn) {
                                  return true;
                                }
                                const key = await verifyCaptcha(undefined, undefined, undefined, selectedIndices);
                                if (key) {
                                  // 验证成功，设置key，不再刷新验证码
                                  setCaptchaKey(key);
                                  setCaptchaError('');
                                  setCaptchaFailCount(0);
                                  return true;
                                }
                                return false;
                              }}
                              onRefresh={loadCaptcha}
                            />
                          )}
                        </div>
                      ) : captcha.image ? (
                        // 兼容旧格式：单图片坐标点击模式（弹窗）
                        <div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setClickDialogOpen(true)}
                            className="w-full"
                            disabled={captchaLoading || !captcha.image}
                          >
                            {captchaLoading ? '加载中...' : '开始图片验证'}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : captcha?.type === 'turnstile' ? (
                    <div className="mt-1">
                      {captcha.sitekey && isBrowserEnvironment ? (
                        <TurnstileCaptcha
                          sitekey={captcha.sitekey}
                          onVerify={async (token) => {
                            if (isLoggedIn) return true;
                            try {
                              const response = await captchaApi.verifyTurnstile(captcha.id, token) as { verified: boolean; key: string } | { data: { verified: boolean; key: string } };
                              const verified = 'data' in response ? response.data.verified : response.verified;
                              const key = 'data' in response ? response.data.key : response.key;
                              if (verified && key) {
                                setCaptchaKey(key);
                                setCaptchaError('');
                                setCaptchaFailCount(0);
                                return true;
                              }
                              return false;
                            } catch {
                              return false;
                            }
                          }}
                          onError={() => {
                            setCaptchaError('安全验证加载失败');
                          }}
                        />
                      ) : (
                        <div className="p-4 text-center text-sm text-red-500">
                          请在浏览器环境中使用此验证码
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-1.5 sm:gap-2 mt-1">
                      <Input
                        id="captcha"
                        type="text"
                        value={captchaCode}
                        onChange={(e) => {
                          setCaptchaCode(e.target.value);
                          setCaptchaKey('');
                          setCaptchaError('');
                        }}
                        placeholder="请输入验证码"
                        maxLength={6}
                        className={`flex-1 h-10 sm:h-9 text-base sm:text-sm ${captchaError ? 'border-red-500' : ''}`}
                        disabled={captchaLoading}
                      />
                      <div 
                        className={`w-18 sm:w-20 md:w-24 h-10 sm:h-9 border rounded-md sm:rounded-lg cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${captchaLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={captchaLoading ? undefined : loadCaptcha}
                        title="点击刷新验证码"
                      >
                        {captchaLoading ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : captcha?.image ? (
                          <Image src={captcha.image} alt="验证码" width={96} height={40} className="h-full object-contain" unoptimized />
                        ) : (
                          <span className="text-xs text-gray-500">加载中</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={loadCaptcha}
                        disabled={captchaLoading}
                        title="刷新验证码"
                        className="flex-shrink-0 h-10 sm:h-9 w-10 sm:w-9"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${captchaLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  )}
                  {captchaError && captcha?.type !== 'slide' && (
                    <p className="text-xs text-red-500 mt-1">{captchaError}</p>
                  )}
                  {captchaFailCount > 0 && captcha?.type !== 'slide' && (
                    <p className="text-xs text-orange-500 mt-1">
                      验证失败 {captchaFailCount} 次，请仔细输入
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loginOperation.loading || (systemInfo?.captcha_enabled && !captchaKey && captcha?.type !== 'turnstile')}
                className="w-full h-10 sm:h-9 md:h-10 text-sm sm:text-base font-medium"
              >
                {loginOperation.loading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    登录中...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2 h-4 w-4" />
                    登录
                  </>
                )}
              </Button>
            </form>
          ) : systemInfo?.allow_registration ? (
            // 注册表单 - 只在允许注册时显示
            <form onSubmit={handleRegister} className="space-y-2.5 sm:space-y-3 md:space-y-4">
              <div>
                <Label htmlFor="register-username" className="text-xs sm:text-sm">用户名</Label>
                <Input
                  id="register-username"
                  type="text"
                  required
                  value={registerForm.username}
                  onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                  placeholder="请输入用户名"
                  className="mt-1 h-10 sm:h-9 text-base sm:text-sm"
                />
              </div>

              <div>
                <Label htmlFor="register-email" className="text-xs sm:text-sm">邮箱</Label>
                <Input
                  id="register-email"
                  type="email"
                  required
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  placeholder="请输入邮箱"
                  className="mt-1 h-10 sm:h-9 text-base sm:text-sm"
                />
              </div>

              <div>
                <Label htmlFor="register-password" className="text-xs sm:text-sm">密码</Label>
                <Input
                  id="register-password"
                  type="password"
                  required
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  placeholder="请输入密码（至少8位）"
                  className="mt-1 h-10 sm:h-9 text-base sm:text-sm"
                />
              </div>

              {/* 验证码 - 注册页面显示所有类型的验证码 */}
              {systemInfo?.captcha_enabled && captcha && (
                <div>
                  <Label htmlFor="register-captcha" className="text-xs sm:text-sm">
                    {captcha.type === 'slide' ? '滑块验证码' : 
                     captcha.type === 'pow' ? 'POW验证码' :
                     captcha.type === 'click' ? '图片点击验证码' :
                     captcha.type === 'turnstile' ? '安全验证' :
                     '图片验证码'}
                  </Label>
                  {captcha.type === 'slide' ? (
                    <div className="mt-1">
                      <div 
                        className={`relative border rounded-lg overflow-hidden bg-white dark:bg-gray-800 transition-all ${
                          captchaKey
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/10 shadow-sm cursor-default'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md cursor-pointer'
                        }`}
                        onClick={() => {
                          if (!captchaKey && !captchaLoading && captcha.image) {
                            setSlideDialogOpen(true);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="点击完成滑块验证"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!captchaKey && !captchaLoading && captcha.image) {
                              setSlideDialogOpen(true);
                            }
                          }
                        }}
                      >
                        <div className="p-3.5">
                          <div className="flex items-center justify-between gap-3">
                            {/* 左侧：复选框和文字 */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* 自定义复选框 */}
                              <div className="flex-shrink-0">
                                {captchaKey ? (
                                  // 已验证：显示选中状态的复选框
                                  <div className="h-7 w-7 border-2 border-green-600 bg-green-600 rounded-[4px] flex items-center justify-center pointer-events-none checkbox">
                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                  </div>
                                ) : (
                                  // 未选中：显示空复选框（灰色边框，白色背景）
                                  <div className="h-7 w-7 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-[4px] flex items-center justify-center pointer-events-none checkbox"></div>
                                )}
                              </div>
                              
                              {/* 文字 */}
                              <div className="flex-1 min-w-0 pointer-events-none">
                                <p 
                                  className={`text-sm font-medium transition-colors ${
                                    captchaKey
                                      ? 'text-green-600 dark:text-green-500'
                                      : 'text-gray-900 dark:text-white'
                                  }`}
                                >
                                  {captchaKey
                                    ? '恭喜您，验证通过！'
                                    : '继续以完成验证...'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  滑块验证
                                </p>
                              </div>
                            </div>
                            
                            {/* 右侧：标识 */}
                            <div className="flex items-center gap-2 flex-shrink-0 pointer-events-none">
                              <div className="flex items-center justify-center w-9 h-9 rounded pointer-events-none">
                                {captchaKey ? (
                                  // 已验证：绿色勾选
                                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ fill: '#63c08e', stroke: '#4CAF50' }}>
                                    <path d="M20 6L9 17l-5-5"/>
                                  </svg>
                                ) : (
                                  // 未选中状态：显示灰色圆形
                                  <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"></div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className={`text-xs font-semibold ${
                                  captchaKey
                                    ? 'text-green-600 dark:text-green-500'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}>
                                  Slider
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {captcha.image && (
                        <SliderCaptchaDialog
                          open={slideDialogOpen}
                          onOpenChange={setSlideDialogOpen}
                          image={captcha.image}
                          onVerify={async (position) => {
                            if (isLoggedIn) return true;
                            const success = await handleSliderVerify(position);
                            if (success) {
                              // 立即关闭对话框，不延迟
                              setSlideDialogOpen(false);
                              return true;
                            }
                            return false;
                          }}
                          onRefresh={loadCaptcha}
                        />
                      )}
                    </div>
                  ) : captcha.type === 'pow' ? (
                    <div className="mt-1">
                      {/* POW验证码显示逻辑与登录页面相同 */}
                      {powComputing ? (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-sm text-blue-600 dark:text-blue-400">正在计算验证码...</span>
                        </div>
                      ) : powSolution && captchaKey ? (
                        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-600 dark:text-green-400">验证通过</span>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-500">请等待验证码自动计算完成</p>
                        </div>
                      )}
                    </div>
                  ) : captcha.type === 'click' ? (
                    <div className="mt-1">
                      {captcha.images && captcha.images.length > 0 ? (
                        <div>
                          <div 
                            className={`relative border rounded-lg overflow-hidden bg-white dark:bg-gray-800 transition-all ${
                              captchaKey
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/10 shadow-sm cursor-default'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md cursor-pointer'
                            }`}
                            onClick={() => {
                              if (!captchaKey && !captchaLoading && captcha.images && captcha.images.length > 0) {
                                setClickDialogOpen(true);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="点击完成图片验证"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!captchaKey && !captchaLoading && captcha.images && captcha.images.length > 0) {
                                  setClickDialogOpen(true);
                                }
                              }
                            }}
                          >
                            <div className="p-3.5">
                              <div className="flex items-center justify-between gap-3">
                                {/* 左侧：复选框和文字 */}
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  {/* 自定义复选框 */}
                                  <div className="flex-shrink-0">
                                    {captchaKey ? (
                                      // 已验证：显示选中状态的复选框
                                      <div className="h-7 w-7 border-2 border-green-600 bg-green-600 rounded-[4px] flex items-center justify-center pointer-events-none checkbox">
                                        <CheckCircle2 className="w-4 h-4 text-white" />
                                      </div>
                                    ) : (
                                      // 未选中：显示空复选框（灰色边框，白色背景）
                                      <div className="h-7 w-7 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-[4px] flex items-center justify-center pointer-events-none checkbox"></div>
                                    )}
                                  </div>
                                  
                                  {/* 文字 */}
                                  <div className="flex-1 min-w-0 pointer-events-none">
                                    <p 
                                      className={`text-sm font-medium transition-colors ${
                                        captchaKey
                                          ? 'text-green-600 dark:text-green-500'
                                          : 'text-gray-900 dark:text-white'
                                      }`}
                                    >
                                      {captchaKey
                                        ? '恭喜您，验证通过！'
                                        : '继续以完成验证...'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      图片验证
                                    </p>
                                  </div>
                                </div>
                                
                                {/* 右侧：标识 */}
                                <div className="flex items-center gap-2 flex-shrink-0 pointer-events-none">
                                  <div className="flex items-center justify-center w-9 h-9 rounded pointer-events-none">
                                    {captchaKey ? (
                                      // 已验证：绿色勾选
                                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ fill: '#63c08e', stroke: '#4CAF50' }}>
                                        <path d="M20 6L9 17l-5-5"/>
                                      </svg>
                                    ) : (
                                      // 未选中状态：显示灰色圆形
                                      <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"></div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className={`text-xs font-semibold ${
                                      captchaKey
                                        ? 'text-green-600 dark:text-green-500'
                                        : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                      Click
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {captcha.images && captcha.correct_indices && captcha.text && (
                            <ClickCaptchaDialog
                              open={clickDialogOpen}
                              onOpenChange={setClickDialogOpen}
                              images={captcha.images}
                              correctIndices={captcha.correct_indices}
                              text={captcha.text}
                              onVerify={async (selectedIndices) => {
                                if (isLoggedIn) return true;
                                const key = await verifyCaptcha(undefined, undefined, undefined, selectedIndices);
                                if (key) {
                                  setCaptchaKey(key);
                                  setCaptchaError('');
                                  setCaptchaFailCount(0);
                                  // 立即关闭对话框，不延迟
                                  setClickDialogOpen(false);
                                  return true;
                                }
                                return false;
                              }}
                              onRefresh={loadCaptcha}
                            />
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : captcha.type === 'turnstile' ? (
                    <div className="mt-1">
                      {captcha.sitekey && isBrowserEnvironment ? (
                        <TurnstileCaptcha
                          sitekey={captcha.sitekey}
                          onVerify={async (token) => {
                            if (isLoggedIn) return true;
                            try {
                              const response = await captchaApi.verifyTurnstile(captcha.id, token) as { verified: boolean; key: string } | { data: { verified: boolean; key: string } };
                              const verified = 'data' in response ? response.data.verified : response.verified;
                              const key = 'data' in response ? response.data.key : response.key;
                              if (verified && key) {
                                setCaptchaKey(key);
                                setCaptchaError('');
                                setCaptchaFailCount(0);
                                return true;
                              }
                              return false;
                            } catch {
                              return false;
                            }
                          }}
                          onError={() => {
                            setCaptchaError('安全验证加载失败');
                          }}
                        />
                      ) : (
                        <div className="p-4 text-center text-sm text-red-500">
                          请在浏览器环境中使用此验证码
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-1.5 sm:gap-2 mt-1">
                      <Input
                        id="register-captcha"
                        type="text"
                        value={captchaCode}
                        onChange={(e) => {
                          setCaptchaCode(e.target.value);
                          setCaptchaKey(''); // 修改验证码时重置验证状态
                          setCaptchaError(''); // 清除错误状态
                        }}
                        placeholder="请输入验证码"
                        maxLength={6}
                        className={`flex-1 h-10 sm:h-9 text-base sm:text-sm ${captchaError ? 'border-red-500' : ''}`}
                        disabled={captchaLoading}
                      />
                      <div 
                        className={`w-18 sm:w-20 md:w-24 h-10 sm:h-9 border rounded-md sm:rounded-lg cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${captchaLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={captchaLoading ? undefined : loadCaptcha}
                        title="点击刷新验证码"
                      >
                        {captchaLoading ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : captcha?.image ? (
                          <Image src={captcha.image} alt="验证码" width={96} height={40} className="h-full object-contain" unoptimized />
                        ) : (
                          <span className="text-xs text-gray-500">加载中</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={loadCaptcha}
                        disabled={captchaLoading}
                        title="刷新验证码"
                        className="flex-shrink-0 h-10 sm:h-9 w-10 sm:w-9"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${captchaLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  )}
                  {captchaError && captcha.type !== 'slide' && captcha.type !== 'pow' && captcha.type !== 'click' && captcha.type !== 'turnstile' && (
                    <p className="text-xs text-red-500 mt-1">{captchaError}</p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={registerOperation.loading || (systemInfo?.captcha_enabled && !captchaKey && captcha?.type !== 'turnstile')}
                className="w-full h-10 sm:h-9 md:h-10 text-sm sm:text-base font-medium"
              >
                {registerOperation.loading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    注册中...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    注册
                  </>
                )}
              </Button>
            </form>
          ) : null}

          <Separator className="my-3 sm:my-4 md:my-6" />

          {/* 切换登录/注册 - 只在允许注册时显示注册链接 */}
          {systemInfo && (
            <div className="text-center">
              {isLogin ? (
                // 登录时：只在允许注册时显示注册链接
                systemInfo.allow_registration && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setIsLogin(false);
                      setCaptchaError('');
                      setCaptchaCode('');
                      setCaptchaKey('');
                      loadCaptcha();
                    }}
                    className="text-xs sm:text-sm md:text-base text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    还没有账户？立即注册
                  </Button>
                )
              ) : (
                // 注册时：总是显示登录链接
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setIsLogin(true);
                    setCaptchaError('');
                    setCaptchaCode('');
                    setCaptchaKey('');
                    loadCaptcha();
                  }}
                  className="text-xs sm:text-sm md:text-base text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  已有账户？立即登录
                </Button>
              )}
            </div>
          )}

          {/* 默认账户提示 */}
          {isLogin && (
            <Card className="mt-2 sm:mt-3 md:mt-4 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900">
              <CardContent className="pt-2 sm:pt-3 md:pt-4">
                <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">
                  💡 测试账户
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  管理员: admin / admin123
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
